import { useContext, useMemo } from 'react';
import { FrontendConfigContext } from '../providers/FrontendConfigProvider';
import {
  useAiEnabledApiConfig,
  useDevxGroupBaseEnabled,
  useFeatureEnabledByLabel,
  useSidebarTypeConfig,
} from './useFeatureFlag';

type FeatureFlags = {
  sidebarType: string;
  sidebarFeatures: string[];
  sidebarFeatureChildren: Record<string, string[]>;
  aiEnabledAPI: any[];
  devxGroupBaseEnable: boolean;
  isReady: boolean;
};

const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  sidebarType: '',
  sidebarFeatures: [],
  sidebarFeatureChildren: {},
  aiEnabledAPI: [],
  devxGroupBaseEnable: false,
  isReady: false,
};

// Every menu feature (parent or child) maps to its own Split flag via
// LABEL_FLAG_MAP in useFeatureFlag.ts. This aggregator composes the atomic
// hooks into the shape sidebar builders and pages already consume.
export const useFeatureFlags = (controlledItems?: any[]): FeatureFlags => {
  const { frontendConfigData } = useContext(FrontendConfigContext);

  const groupBaseEnabled = useDevxGroupBaseEnabled();
  const { config: sidebarTypeConfig } = useSidebarTypeConfig();
  const { config: aiEnabledApiConfig } = useAiEnabledApiConfig();
  const enabledByLabel = useFeatureEnabledByLabel();

  const isLocalDevelopment =
    typeof window !== 'undefined' &&
    ['localhost', '127.0.0.1'].includes(window.location.hostname);

  return useMemo(() => {
    if (isLocalDevelopment && controlledItems) {
      return {
        ...DEFAULT_FEATURE_FLAGS,
        sidebarFeatures: controlledItems.map(item => item.label || item.name),
        sidebarFeatureChildren: Object.fromEntries(
          controlledItems
            .filter(item => item.children)
            .map(item => [
              item.label || item.name,
              item.children.map((child: any) => child.label || child.name),
            ]),
        ),
        devxGroupBaseEnable: true,
        isReady: true,
      };
    }

    if (!frontendConfigData) {
      return DEFAULT_FEATURE_FLAGS;
    }

    const sidebarType =
      typeof sidebarTypeConfig?.sidebarType === 'string'
        ? sidebarTypeConfig.sidebarType
        : typeof sidebarTypeConfig?.type === 'string'
        ? sidebarTypeConfig.type
        : '';

    const configuredApis = aiEnabledApiConfig?.apis;
    const aiEnabledAPI = Array.isArray(configuredApis)
      ? configuredApis
      : Object.entries(configuredApis ?? {}).map(
          ([name, api]: [string, any]) => ({ name, ...api }),
        );

    const isLabelEnabled = (label: string) =>
      groupBaseEnabled && enabledByLabel[label] === true;

    const sidebarFeatures = Object.keys(enabledByLabel).filter(isLabelEnabled);

    return {
      sidebarType,
      sidebarFeatures,
      sidebarFeatureChildren: Object.fromEntries(
        (controlledItems ?? [])
          .filter(item => item.children)
          .map(item => [
            item.label || item.name,
            item.children
              .map((child: any) => child.label || child.name)
              .filter(isLabelEnabled),
          ]),
      ),
      aiEnabledAPI: groupBaseEnabled ? aiEnabledAPI : [],
      devxGroupBaseEnable: groupBaseEnabled,
      isReady: true,
    };
  }, [
    frontendConfigData,
    groupBaseEnabled,
    sidebarTypeConfig,
    aiEnabledApiConfig,
    enabledByLabel,
    isLocalDevelopment,
    controlledItems,
  ]);
};
